import React from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, MapPin, Handshake, Search, Sparkles, Mail, ArrowRight } from "lucide-react";
import { useLang } from "../lib/i18n";

const C = {
  en: {
    tag: "About Koila Holdings",
    title: "Real assets. Real people. One trusted marketplace for Cameroon.",
    intro:
      "Koila Holdings is Cameroon's premium marketplace for land, houses, cars, rentals, leases and modular container structures. We bring buyers, tenants, sellers and agents together in one elegant, transparent place — from a titled plot in Buea to a furnished apartment in Douala or a converted 40ft container in Limbe.",
    missionTitle: "Our mission",
    mission:
      "To make buying, renting and leasing property and vehicles in Cameroon simple, safe and dignified. We believe every genuine listing deserves a beautiful showcase, and every buyer deserves to reach the owner directly — without middlemen, hidden fees or guesswork.",
    offerTitle: "What we offer",
    offer: [
      { icon: Search, h: "Everything in one place", p: "Land, houses, cars, rentals, leases and containers — search, filter and compare across all ten regions." },
      { icon: ShieldCheck, h: "Verified before it's live", p: "Every listing is reviewed and payment-confirmed by our team before it appears, so you browse with confidence." },
      { icon: MapPin, h: "Know exactly where", p: "Detailed location — street, town, division, sub-division and an interactive map — so you can find a property with ease." },
      { icon: Handshake, h: "Talk to owners directly", p: "Reach sellers and agents instantly by WhatsApp, phone or email. No intermediaries, no hidden commissions." },
    ],
    howTitle: "How it works",
    how: [
      "Sellers post their property with photos, an optional video tour and precise location.",
      "A small listing fee is paid securely via MTN Mobile Money and confirmed by our team.",
      "Once published, buyers browse, save favourites and contact the seller directly.",
    ],
    ctaTitle: "Have something to list?",
    ctaText: "Join thousands of Cameroonians finding land, homes and vehicles the honest way.",
    ctaBtn: "Post a listing",
    contact: "Questions? Write to us at",
  },
  fr: {
    tag: "À propos de Koila Holdings",
    title: "De vrais biens. De vraies personnes. Une marketplace de confiance pour le Cameroun.",
    intro:
      "Koila Holdings est la marketplace premium du Cameroun pour les terrains, maisons, voitures, locations, baux et conteneurs aménagés. Nous réunissons acheteurs, locataires, vendeurs et agents dans un espace élégant et transparent — d'un terrain titré à Buea à un appartement meublé à Douala ou un conteneur de 40 pieds à Limbe.",
    missionTitle: "Notre mission",
    mission:
      "Rendre l'achat, la location et le bail de biens et de véhicules au Cameroun simples, sûrs et dignes. Chaque annonce authentique mérite une belle vitrine, et chaque acheteur mérite de joindre le propriétaire directement — sans intermédiaires, frais cachés ni incertitude.",
    offerTitle: "Ce que nous offrons",
    offer: [
      { icon: Search, h: "Tout au même endroit", p: "Terrains, maisons, voitures, locations, baux et conteneurs — recherchez, filtrez et comparez dans les dix régions." },
      { icon: ShieldCheck, h: "Vérifié avant publication", p: "Chaque annonce est examinée et le paiement confirmé par notre équipe avant sa mise en ligne." },
      { icon: MapPin, h: "Savoir exactement où", p: "Localisation détaillée — rue, ville, département, arrondissement et carte interactive — pour trouver facilement." },
      { icon: Handshake, h: "Parlez aux propriétaires", p: "Contactez vendeurs et agents par WhatsApp, téléphone ou e-mail. Sans intermédiaires ni commissions cachées." },
    ],
    howTitle: "Comment ça marche",
    how: [
      "Les vendeurs publient leur bien avec photos, une visite vidéo optionnelle et la localisation précise.",
      "Des frais d'annonce modestes sont payés en toute sécurité via MTN Mobile Money et confirmés par notre équipe.",
      "Une fois publiée, les acheteurs explorent, enregistrent leurs favoris et contactent le vendeur directement.",
    ],
    ctaTitle: "Un bien à publier ?",
    ctaText: "Rejoignez des milliers de Camerounais qui trouvent terrains, maisons et véhicules en toute honnêteté.",
    ctaBtn: "Publier une annonce",
    contact: "Des questions ? Écrivez-nous à",
  },
};

export default function About() {
  const { lang } = useLang();
  const c = C[lang];

  return (
    <div className="pt-16">
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-white/10">
        <img src="https://images.unsplash.com/photo-1706855203772-c249b75fe016" alt="" className="absolute inset-0 w-full h-full object-cover opacity-20" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/85 to-[#0A0A0A]/70" />
        <div className="relative z-10 max-w-[1000px] mx-auto px-5 md:px-10 py-20 fade-up">
          <span className="inline-block text-gold text-xs uppercase tracking-[0.3em] border border-gold/40 px-4 py-1.5">{c.tag}</span>
          <h1 className="font-display text-4xl md:text-5xl text-white leading-tight mt-6 max-w-3xl">{c.title}</h1>
          <p className="text-white/60 text-lg mt-6 max-w-2xl leading-relaxed">{c.intro}</p>
        </div>
      </section>

      {/* Mission */}
      <section className="max-w-[1000px] mx-auto px-5 md:px-10 py-16">
        <div className="border-l-2 border-gold pl-6">
          <div className="flex items-center gap-2 text-gold mb-3"><Sparkles size={18} /><h2 className="font-display text-2xl">{c.missionTitle}</h2></div>
          <p className="text-white/70 text-lg leading-relaxed max-w-2xl">{c.mission}</p>
        </div>
      </section>

      {/* Offer */}
      <section className="max-w-[1000px] mx-auto px-5 md:px-10 pb-16">
        <h2 className="font-display text-2xl md:text-3xl text-white mb-8">{c.offerTitle}</h2>
        <div className="grid sm:grid-cols-2 gap-6">
          {c.offer.map((o, i) => {
            const Icon = o.icon;
            return (
              <div key={i} className="bg-[#121212] border border-white/10 p-6 hover:border-gold/40 transition-colors" data-testid={`about-offer-${i}`}>
                <Icon size={26} strokeWidth={1.5} className="text-gold" />
                <h3 className="text-white font-medium mt-4">{o.h}</h3>
                <p className="text-white/50 text-sm mt-2 leading-relaxed">{o.p}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* How it works */}
      <section className="max-w-[1000px] mx-auto px-5 md:px-10 pb-16">
        <h2 className="font-display text-2xl md:text-3xl text-white mb-8">{c.howTitle}</h2>
        <div className="grid sm:grid-cols-3 gap-6">
          {c.how.map((step, i) => (
            <div key={i} className="border-t-2 border-gold/50 pt-4">
              <span className="font-display text-gold text-3xl">0{i + 1}</span>
              <p className="text-white/70 mt-2 leading-relaxed">{step}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-[1000px] mx-auto px-5 md:px-10 pb-20">
        <div className="bg-[#121212] border border-white/10 p-10 md:p-14 text-center">
          <h2 className="font-display text-3xl text-white">{c.ctaTitle}</h2>
          <p className="text-white/60 mt-3 max-w-xl mx-auto">{c.ctaText}</p>
          <Link to="/post" data-testid="about-cta-btn" className="mt-8 inline-flex items-center gap-2 bg-gold text-black px-7 py-3 font-medium hover:bg-gold-light transition-colors">
            {c.ctaBtn} <ArrowRight size={18} />
          </Link>
          <p className="text-white/40 text-sm mt-8 flex items-center justify-center gap-2">
            <Mail size={15} className="text-gold" /> {c.contact} <a href="mailto:info@koilaholdings.com" className="text-gold hover:opacity-80">info@koilaholdings.com</a>
          </p>
        </div>
      </section>
    </div>
  );
}
